import React, { useContext } from 'react';

import { useUserProfile } from '@atom/hooks/useUserProfile';
import { Button } from '@atom/mui';
import colors from '@atom/styles/colors';
import { ARDOTAttribute, AssetRequestStatus } from '@atom/types/assetRequest';
import { hasRolePermissions, ROLE_SETS } from '@atom/utilities/authUtilities';
import { getDisplayDateTime } from '@atom/utilities/timeUtilities';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

import { ardotAttributeDictionaryAssetDetail } from '../../customTenantUtilities';
import ViewRequestContext from '../ViewRequestContext';

import ReceiverSection from './ReceiverSection';
import ReviewerSection from './ReviewerSection';

import './ardotView.css';

const styles = {
  cancelButton: {
    width: '100%',
    color: colors.brand.red,
    border: `1px solid ${colors.neutral.ash}`,
    marginTop: '1rem',
  },
};

const ARDOT118TransferView = () => {
  const userProfile = useUserProfile();
  const { activeRequest, fromAsset, setStatus, setConfirmOpen } = useContext(
    ViewRequestContext,
  );
  const attributeDictionary = ardotAttributeDictionaryAssetDetail(fromAsset);

  const createdByText = `Created by ${activeRequest?.createdBy?.firstName} ${
    activeRequest?.createdBy?.lastName
  } on ${getDisplayDateTime(activeRequest?.createdDate)}`;

  const canceledByText = `Canceled by ${activeRequest?.updatedBy?.firstName} ${
    activeRequest?.updatedBy?.lastName
  } on ${getDisplayDateTime(activeRequest?.cancelDate)}`;

  const handleCancel = () => {
    setStatus(AssetRequestStatus.CANCELED);
    setConfirmOpen(true);
  };

  const showCanceledByText =
    activeRequest?.status === AssetRequestStatus.CANCELED;
  const showCancel =
    (userProfile?.userId === activeRequest?.createdBy?.id ||
      hasRolePermissions(ROLE_SETS.ORG_ADMIN)) &&
    (activeRequest?.status === AssetRequestStatus.PENDING_REVIEW ||
      activeRequest?.status === AssetRequestStatus.PENDING_APPROVAL);

  return (
    <>
      <div>
        <div styleName="row-title">Tag Number</div>
        <div>{attributeDictionary[ARDOTAttribute.TAG_NUMBER]?.value || ''}</div>
      </div>
      <div styleName="view-row">
        <div styleName="row-title">Description</div>
        <div>
          {attributeDictionary[ARDOTAttribute.DESCRIPTION]?.value || ''}
        </div>
      </div>
      {!isNilOrEmpty(activeRequest?.attributes) && (
        <>
          <div styleName="section-title">{`Update Attributes (${activeRequest?.attributes.length})`}</div>
          <div styleName="section-subtext">
            Update the inventory attributes once the request is approved.
          </div>
          <>
            {activeRequest?.attributes.map(attribute => (
              <div key={attribute?.id} styleName="view-row">
                <div styleName="row-title">{attribute?.name}</div>
                <div>
                  <span styleName="old-value">{attribute?.oldValue}</span>
                  <span styleName="new-value">{attribute?.newValue}</span>
                </div>
              </div>
            ))}
          </>
        </>
      )}
      {activeRequest?.toCategoryId && (
        <>
          <div styleName="section-title">Transfer</div>
          <div styleName="section-subtext">
            Transfer the inventory to another district or division once the
            request is approved.
          </div>
          <div styleName="view-row">
            <div styleName="row-title">From District or Division</div>
            <div>{activeRequest?.categoryPath || ''}</div>
          </div>
          <div styleName="view-row">
            <div styleName="row-title">To District or Division</div>
            <div>{activeRequest?.toCategoryPath || ''}</div>
          </div>
        </>
      )}
      <div styleName="section-title">Approval</div>
      <ReviewerSection />
      <ReceiverSection />
      {showCancel && (
        <Button style={styles.cancelButton} onClick={handleCancel}>
          Cancel Request
        </Button>
      )}
      <div styleName="created-subtext">{createdByText}</div>
      {showCanceledByText && (
        <div styleName="created-subtext">{canceledByText}</div>
      )}
    </>
  );
};

export default ARDOT118TransferView;
